
import { useState, useEffect } from 'react';
import { ContentProcessorConfig, ContentProcessorResult, ContentType } from './types';
import { detectContentType } from './utils/contentTypeDetector';
import { handlers } from './handlers';
import { getFallbackContent } from './utils/fallbackContent';
import { processHtml } from './utils/htmlProcessor';

/**
 * Hook for processing different types of content with specialized handlers
 */
export const useContentProcessor = (
  content: string | null | undefined,
  config: ContentProcessorConfig = {}
): ContentProcessorResult => {
  const [processedContent, setProcessedContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentType, setContentType] = useState<ContentType>('unknown');

  useEffect(() => {
    // Don't process if content is null/undefined
    if (content === null || content === undefined) {
      setProcessedContent(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    
    // Start processing
    setIsLoading(true);
    setError(null);
    
    try {
      // Auto-detect content type if not provided
      const detectedType = config.type || detectContentType(content);
      setContentType(detectedType);
      
      // Process content through handlers chain
      let processedResult = content;
      
      // Apply all handlers in sequence
      for (const handler of handlers) {
        const result = handler(processedResult, config);
        
        // If handler returns null, skip to next handler
        if (result !== null) {
          processedResult = result;
        }
      }
      
      // HTML-specific processing
      if (detectedType === 'html') {
        // Fix: Pass the processedResult as the first argument (string)
        // and config as the second argument (ContentProcessorConfig)
        processedResult = processHtml(processedResult, config);
      }
      
      // Set the final processed content
      setProcessedContent(processedResult);
      setIsLoading(false);
    } catch (e) {
      console.error('Content processing error:', e);
      setError(`Error processing content: ${e instanceof Error ? e.message : String(e)}`);
      
      // In production, provide fallback content for errors
      if (config.slug) {
        setProcessedContent(getFallbackContent(config.slug));
      } else {
        setProcessedContent(null);
      }
      
      setIsLoading(false);
    }
  }, [content, config]);
  
  return { processedContent, isLoading, error, contentType };
};
